/**
 * Serializer Registry
 *
 * Allows plugins to register serializers for custom data types.
 * Used by endpoints with pushToStore: true to serialize values before storing.
 */

type Serializer = (value: any) => any;

const serializers = new Map<string, Serializer>();

// Primitive types that don't need serialization
const PRIMITIVE_TYPES = new Set(['number', 'string', 'boolean', 'array', 'object']);

/**
 * Register a serializer for a data type.
 * Called by plugins during registration.
 */
export function registerSerializer(dataType: string, serializer: Serializer): void {
  serializers.set(dataType, serializer);
}

/**
 * Serialize a value for storage.
 * - Primitive types pass through unchanged
 * - Custom types require a registered serializer
 * - Throws if no serializer found for non-primitive type
 */
export function serialize(dataType: string, value: any): any {
  if (value === null || value === undefined) {
    return value;
  }

  // Primitive types don't need serialization
  if (PRIMITIVE_TYPES.has(dataType)) {
    return value;
  }

  const serializer = serializers.get(dataType);
  if (!serializer) {
    throw new Error(
      `No serializer registered for dataType "${dataType}". ` +
      `Either register a serializer or don't use pushToStore: true for this type.`
    );
  }

  return serializer(value);
}

/**
 * Check if a serializer exists for a data type.
 */
export function hasSerializer(dataType: string): boolean {
  return PRIMITIVE_TYPES.has(dataType) || serializers.has(dataType);
}
